/* ---------------------------------------------------------------------------
   The modeller's inspectable-component tree, and the two names every node has.

   The tree comes live from the hero viewport over `postMessage` — Spans >
   Span-1 > Deck-1, Abutments > Abutment-1 > Bearing Lines > Deck-1 > B-3 — and
   exists only in that frame's memory while a model is loaded. See ComponentNode
   in ./types.ts and src/modeler/viewport.ts.

   WHY THIS IS A MODULE. Two screens walk that tree for different reasons: the
   Defects card places stored defects onto it, and the "Log defect" dialog offers
   it as a picker. Both need the same non-obvious naming rule, and both had their
   own copy of it — which is exactly the shape of thing that drifts, and where a
   drift would show up as defects quietly failing to place.

   THE RULE. A node answers to two names:

     label      what the tree calls it, e.g. "B-3"
     qualified  what the modeller calls the MESH — its non-group ancestors
                concatenated, e.g. "Abutment-1 Bearing Lines Deck-1 B-3"

   A defect stores one string, and which of the two it got depends on where it
   was logged: picked from a list it gets the label, picked from the 3D view it
   gets the mesh name (see `${label} Bent Cap` in the vendored App.ts and the
   comment there about walking the parent chain). The modeller has this mismatch
   too and shows the casualties under its own "Loose Defects (from 3D pick)".

   A GROUP CONTRIBUTES NOTHING to a qualified name. "Abutments" is a work
   breakdown, not a thing in the world, so the prefix passes through it
   untouched. That is why `qualified` is threaded down the walk rather than
   derived from the path.
   --------------------------------------------------------------------------- */

import type { ComponentNode } from './types.ts';

/**
 * A node's label with a trailing count taken off — "Abutment (22)" -> "Abutment".
 *
 * WHY THIS EXISTS. An imported model's tree is grouped by the source's own object
 * type, and the vendored `BuildImportedMeshBranch` labels each group
 * `${type} (${n})` — the count is in the NAME. A defect stored against
 * "Imported Model Abutment (22) Front Wall" is stored against a part count, so
 * re-importing the same bridge with one abutment part more or fewer renames the
 * group and strands every defect logged under the old name. They are not lost —
 * the card shows them under "Not on this model" — but nothing about that is the
 * inspector's fault and nothing tells them why.
 *
 * So the count is stripped from what is STORED and kept in what is DRAWN: the
 * picker still shows "Abutment (22)", because how many parts are in a group is
 * worth knowing, and the row's value is the stable half of it.
 *
 * Only a trailing parenthesised integer is removed. A component genuinely called
 * "Deck (upper)" or "Pier 2 (north)" keeps its name — this is not a general
 * bracket-stripper, it is the one pattern the vendored grouping emits.
 */
export function bareLabel(label: string): string {
  return label.replace(/ \(\d+\)$/, '');
}

/** One node, as a walk hands it over. */
export interface TreeVisit {
  node: ComponentNode;
  /**
   * Label path from the root, e.g. `>Abutments>Abutment-1`.
   *
   * The identity of a POSITION in the tree, and what folding is keyed on. Not
   * unique across models and not stored anywhere — two bents both have a node
   * called "Bent Cap" and their paths are what tells them apart.
   */
  path: string;
  /** Concatenated non-group ancestors including this node, or '' for a group. */
  qualified: string;
  /** 0 for a root. */
  depth: number;
  /** The path of the node above, or '' at the root. */
  parentPath: string;
}

/**
 * Depth-first walk, parents before children.
 *
 * Every node is visited: whether a subtree is worth drawing is a fact about its
 * descendants, so a caller that folds still has to see what it is folding. The
 * whole tree is a few hundred nodes at its largest and this builds no DOM.
 */
export function walkComponentTree(
  roots: readonly ComponentNode[],
  visit: (entry: TreeVisit) => void,
): void {
  const step = (
    nodes: readonly ComponentNode[],
    parentPath: string,
    prefix: string,
    depth: number,
  ): void => {
    for (const node of nodes) {
      const path = `${parentPath}>${node.label}`;
      const bare = bareLabel(node.label);
      const qualified = node.isComponent ? (prefix ? `${prefix} ${bare}` : bare) : prefix;
      visit({ node, path, qualified: node.isComponent ? qualified : '', depth, parentPath });
      step(node.children, path, qualified, depth + 1);
    }
  };
  step(roots, '', '', 0);
}

/**
 * Every name a node answers to, mapped to the paths that claim it.
 *
 * What the Defects card places stored defects with. A name claimed by more than
 * one path is ambiguous — "Bent Cap" on a five-bent viaduct names five nodes —
 * and the card leaves those unplaced rather than guessing, because repeating one
 * defect under each would report five where the inspector logged one.
 */
export function indexComponentNames(roots: readonly ComponentNode[]): Map<string, string[]> {
  const into = new Map<string, string[]>();
  walkComponentTree(roots, ({ node, path, qualified }) => {
    if (!node.isComponent) return;
    /* Both names, and the BARE label rather than the drawn one — a defect stored
       before the count was stripped still has to place, and one stored after must
       not stop placing when the count moves. */
    const bare = bareLabel(node.label);
    for (const name of qualified === bare ? [bare] : [qualified, bare]) {
      const claims = into.get(name);
      if (claims) claims.push(path);
      else into.set(name, [path]);
    }
  });
  return into;
}

/**
 * Every model id the tree's components carry.
 *
 * What the Defects card places by first. An imported model's names repeat — three
 * spans each have a "G-1", and their label paths are the same too — so neither a
 * name nor a path can say which girder a defect is on, while the id it was stored
 * with can.
 */
export function indexComponentIds(roots: readonly ComponentNode[]): Set<string> {
  const into = new Set<string>();
  walkComponentTree(roots, ({ node }) => {
    if (node.isComponent && node.id) into.add(node.id);
  });
  return into;
}

/** The paths of the top-level nodes. What the picker opens by default. */
export function rootPaths(roots: readonly ComponentNode[]): string[] {
  return roots.map((node) => `>${node.label}`);
}

/**
 * Every path that has to be open for `name` to be on screen, plus the tops.
 *
 * Used when the dialog opens against a defect that already has a component: a
 * picker that hides the current answer is one that invites somebody to pick a
 * different one by accident.
 *
 * A name claimed by several nodes opens the ancestors of ALL of them. That is
 * the honest response to an ambiguous name in a picker — unlike on the card,
 * where showing one defect under five components would misreport the count.
 */
export function pathsToReveal(roots: readonly ComponentNode[], name: string): string[] {
  const out = new Set(rootPaths(roots));
  if (name === '') return [...out];

  walkComponentTree(roots, ({ node, path, qualified }) => {
    if (!node.isComponent) return;
    if (qualified !== name && bareLabel(node.label) !== name) return;
    // Every ancestor of this node, taken off its own path: `>A>B>C` opens `>A`
    // and `>A>B`. The node itself is not opened — being visible is not the same
    // as being expanded, and expanding a leaf means nothing.
    const parts = path.split('>').filter(Boolean);
    for (let i = 1; i < parts.length; i++) out.add(`>${parts.slice(0, i).join('>')}`);
  });
  return [...out];
}

/** One row of the picker, in the order it should be drawn. */
export interface PickerRow {
  path: string;
  /** What to print — the node's own label, not the qualified name. */
  label: string;
  /** What to store when this row is chosen. '' for a group, which cannot be. */
  value: string;
  depth: number;
  /** Whether it has children to reveal, and so earns a twisty. */
  hasChildren: boolean;
  isOpen: boolean;
  /** False for a work-breakdown group, which nests children and carries no defect. */
  isComponent: boolean;
  /** The model's id for the component, where it has one — stored beside `value`. */
  id: string | null;
}

/**
 * The tree flattened to the rows a picker should draw, given what is open.
 *
 * Closed nodes keep their own row and drop their descendants — the opposite of
 * the Defects card, which drops a branch with nothing wrong with it entirely.
 * A picker has to be able to reach every component, including the sound ones.
 */
export function pickerRows(
  roots: readonly ComponentNode[],
  open: ReadonlySet<string>,
): PickerRow[] {
  const out: PickerRow[] = [];

  const step = (nodes: readonly ComponentNode[], parentPath: string, prefix: string, depth: number): void => {
    for (const node of nodes) {
      const path = `${parentPath}>${node.label}`;
      const bare = bareLabel(node.label);
      const qualified = node.isComponent ? (prefix ? `${prefix} ${bare}` : bare) : prefix;
      const isOpen = open.has(path);
      out.push({
        path,
        label: node.label,
        value: node.isComponent ? qualified : '',
        depth,
        hasChildren: node.children.length > 0,
        isOpen,
        isComponent: node.isComponent,
        id: node.isComponent ? (node.id ?? null) : null,
      });
      if (isOpen) step(node.children, path, qualified, depth + 1);
    }
  };

  step(roots, '', '', 0);
  return out;
}

/**
 * Every component whose qualified name contains `query`, as flat picker rows.
 *
 * What the picker's find box draws in place of the tree. Flat rather than the
 * tree with non-matching branches pruned, because the question being answered
 * is "which of these is it" and the qualified name already says where each one
 * is. Case-insensitive; groups never match, since a defect cannot be on one.
 *
 * Capped, because a one-letter query on a large viaduct matches most of it and
 * a thousand buttons is not an answer anybody reads.
 */
export function searchComponents(
  roots: readonly ComponentNode[],
  query: string,
  limit = 60,
): PickerRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const out: PickerRow[] = [];

  const step = (nodes: readonly ComponentNode[], parentPath: string, prefix: string): void => {
    for (const node of nodes) {
      if (out.length >= limit) return;
      const path = `${parentPath}>${node.label}`;
      const bare = bareLabel(node.label);
      const qualified = node.isComponent ? (prefix ? `${prefix} ${bare}` : bare) : prefix;
      if (node.isComponent && qualified.toLowerCase().includes(needle)) {
        out.push({
          path,
          label: qualified,
          value: qualified,
          depth: 0,
          hasChildren: false,
          isOpen: false,
          isComponent: true,
          id: node.id ?? null,
        });
      }
      step(node.children, path, qualified);
    }
  };

  step(roots, '', '');
  return out;
}

/**
 * A stored component name as a trail of labels — "Abutment-1 › Bearing Lines ›
 * Deck-1 › B-3" — or the name itself where the tree does not have it.
 *
 * The qualified name is space-joined, so on a deep node it reads as one long
 * phrase and the reader has to guess where the component boundaries are. This
 * splits it back on the tree that produced it.
 *
 * Falls back to the raw name rather than to nothing. A defect can name a
 * component that was renamed, deleted, or logged against a different model of
 * the same structure, and the register shows what was recorded either way.
 */
export function componentTrail(roots: readonly ComponentNode[], name: string): string[] {
  if (name === '') return [];
  let found: string[] | null = null;

  walkComponentTree(roots, ({ node, path, qualified }) => {
    if (found || !node.isComponent) return;
    if (qualified !== name && bareLabel(node.label) !== name) return;
    /* The path carries groups and the trail should not: "Abutments" is not a
       step in "where on the structure", it is where the modeller filed it. */
    const labels = path.split('>').filter(Boolean);
    found = labels
      .filter((_, i) => i === labels.length - 1 || isComponentAt(roots, labels.slice(0, i + 1)))
      .map(bareLabel);
  });

  return found ?? [name];
}

/** Whether the node at this label path is a component rather than a group. */
function isComponentAt(roots: readonly ComponentNode[], labels: readonly string[]): boolean {
  let nodes: readonly ComponentNode[] = roots;
  let node: ComponentNode | undefined;
  for (const label of labels) {
    node = nodes.find((n) => n.label === label);
    if (!node) return false;
    nodes = node.children;
  }
  return node?.isComponent ?? false;
}

/* ---------------------------------------------------------------------------
   THE SAME RULE, READ OFF THE GEOMETRY.

   Everything above walks a `ComponentNode` tree. What follows produces one — and
   answers the one question a tree cannot: given the node a raycast actually hit,
   which component is that?

   Both live here rather than in the two views that need them, because they ARE
   the naming rule and there is now more than one caller. The record's mesh view
   (src/modeler/meshView.ts) builds the tree the Defects card and the "Log defect"
   picker hang off; the Defect Inspector's 3D view (src/inspector/scene.ts) names
   what a finger landed on. Those two answers have to be the same string, or a
   defect placed by tapping a pier lands under "Not on this model" beside the
   pier it was tapped on. Two copies of this rule is exactly the shape of thing
   that drifts.
   --------------------------------------------------------------------------- */

/**
 * As much of a loaded glTF node as the naming rule reads.
 *
 * Structural on purpose: three.js's `Object3D` satisfies it as it stands, so
 * neither caller converts anything and this module imports no 3D library — which
 * is what keeps it testable against plain objects, and keeps three.js out of
 * every bundle that touches a component name.
 */
export interface MeshNode {
  name: string;
  userData?: Record<string, unknown>;
  /** True on a node with geometry of its own. `Mesh` carries it; `Object3D` does not. */
  isMesh?: boolean;
  children?: readonly MeshNode[];
  parent?: MeshNode | null;
}

/**
 * A node's real label, whatever the loader did to its name.
 *
 * three.js SANITISES node names — `PropertyBinding.sanitizeNodeName` replaces
 * spaces, dots and brackets with underscores — so `object.name` is "PierBent_1"
 * where the export said "PierBent 1". functions/lib/meshGlb.ts therefore writes
 * the true string to `extras.label` as well, which `GLTFLoader` copies into
 * `userData` untouched. Reading the sanitised name instead would rename every
 * component in the register's vocabulary and strand every defect logged against
 * one; reversing the mangling is not possible, because a part genuinely called
 * "Pier_2" is indistinguishable from "Pier 2" once it has been through.
 */
export function nodeLabel(node: MeshNode): string {
  const label = node.userData?.['label'];
  return typeof label === 'string' && label.length > 0 ? label : node.name;
}

/**
 * The glTF node hierarchy as the component tree the register already speaks.
 *
 * `isComponent` is the one judgement: a node with geometry of its own is a thing
 * in the world and can carry a defect; a node that only nests others is a work
 * breakdown ("PierBent (36)") and cannot. That is exactly what
 * `ComponentNode.isComponent` carries and the same distinction the vendored
 * `BuildInspectableTree` makes with its hardcoded set of group labels — except
 * that here it is a property of the geometry rather than a list to keep in step.
 *
 * The root's own CHILDREN, not the root: `GLTFLoader` wraps everything in a
 * `Scene` whose name is the file's, and the register's tree starts at "Imported
 * Model".
 *
 * Given `boxOf`, the parts are regrouped by where they are on the structure —
 * see `groupByLocation`.
 */
export function componentTreeOf(
  root: MeshNode,
  boxOf?: (node: MeshNode) => PartBox | null,
): ComponentNode[] {
  const boxes = new Map<ComponentNode, PartBox>();
  const walk = (node: MeshNode): ComponentNode => {
    const id = node.isMesh === true ? nodeId(node) : null;
    const out: ComponentNode = {
      label: nodeLabel(node),
      isComponent: node.isMesh === true,
      children: (node.children ?? []).map(walk),
      ...(id !== null ? { id } : {}),
    };
    const box = node.isMesh === true && boxOf ? boxOf(node) : null;
    if (box) boxes.set(out, box);
    return out;
  };
  const tree = (root.children ?? []).map(walk);
  return boxOf ? groupByLocation(tree, boxes) : tree;
}

/* ---------------------------------------------------------------------------
   GROUPED BY WHERE A PART IS, NOT ONLY BY WHAT IT IS.

   The export groups by object type and nothing else, and its part names repeat
   from span to span: every span of a girder bridge has its own "D1-D5" and
   "G-1", and every footing its own "Pile-1". Listed under "Diaphragm (36)" the
   four "D1-D5"s cannot be told apart, so a defect cannot be entered against the
   right one. The export says nothing about spans, but the geometry does.

   So the parts are placed along the bridge. The axis is the principal direction
   of the parts' centres in plan (y is up). The substructure — anything whose
   type names an abutment, pier, bent, column, footing, pile, cap or bearing —
   is merged into supports wherever their extents along that axis overlap,
   which keeps a bent's bearings, cap, columns, footings and piles together
   without a distance to tune. Every other part falls in the span between the
   two supports either side of its centre, or under "Whole bridge" when it
   reaches across all of them, as a traffic lane does.

   Inside a support or span the parts keep their type groups, and two meshes
   carrying one id — a bearing drawn as two plates — are one row. Where names
   still repeat (four "Pile-1" under two footings), each part goes under a group
   named after the nearest part whose name does not.

   EVERY NODE ADDED IS A GROUP, and a group contributes nothing to a qualified
   name, so what a defect stores — and what a 3D pick writes — does not change.
   A tree that is not the export's flat shape, or one with no substructure to
   place spans between, is returned as it was.
   --------------------------------------------------------------------------- */

/** A part's extent, world space, as three.js's `Box3` gives it. */
export interface PartBox {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
}

const SUPPORT_TYPE = /abut|pier|bent|column|foot|found|pile|cap|bearing|pylon|tower/i;

interface Placed {
  node: ComponentNode;
  type: string;
  /** Centre along the axis, and the extent either side. */
  s: number;
  lo: number;
  hi: number;
  /** Centre in plan, for the nearest-part grouping. */
  x: number;
  z: number;
}

/** `${label} (${n})`, the export's own way of naming a group. */
function group(label: string, children: ComponentNode[]): ComponentNode {
  return { label: `${label} (${children.length})`, isComponent: false, children };
}

export function groupByLocation(
  roots: readonly ComponentNode[],
  boxes: ReadonlyMap<ComponentNode, PartBox>,
): ComponentNode[] {
  /* The export's shape only: one root group, type groups under it, parts under those. */
  if (roots.length !== 1) return [...roots];
  const top = roots[0]!;
  if (top.isComponent) return [...roots];
  const raw: { node: ComponentNode; type: string; box: PartBox }[] = [];
  for (const typeGroup of top.children) {
    if (typeGroup.isComponent) return [...roots];
    for (const part of typeGroup.children) {
      const box = boxes.get(part);
      if (!part.isComponent || part.children.length || !box) return [...roots];
      raw.push({ node: part, type: bareLabel(typeGroup.label), box });
    }
  }
  if (!raw.length) return [...roots];

  /* The axis: principal direction of the centres in plan. */
  const cx = raw.map((p) => (p.box.min.x + p.box.max.x) / 2);
  const cz = raw.map((p) => (p.box.min.z + p.box.max.z) / 2);
  const mx = cx.reduce((a, v) => a + v, 0) / raw.length;
  const mz = cz.reduce((a, v) => a + v, 0) / raw.length;
  let sxx = 0;
  let szz = 0;
  let sxz = 0;
  for (let i = 0; i < raw.length; i++) {
    const dx = cx[i]! - mx;
    const dz = cz[i]! - mz;
    sxx += dx * dx;
    szz += dz * dz;
    sxz += dx * dz;
  }
  const angle = 0.5 * Math.atan2(2 * sxz, sxx - szz);
  let ux = Math.cos(angle);
  let uz = Math.sin(angle);
  /* One direction of the two, the same every time for the same model. */
  if (Math.abs(ux) >= Math.abs(uz) ? ux < 0 : uz < 0) {
    ux = -ux;
    uz = -uz;
  }
  const along = (x: number, z: number): number => (x - mx) * ux + (z - mz) * uz;

  const placed: Placed[] = raw.map((p, i) => {
    const corners = [
      along(p.box.min.x, p.box.min.z),
      along(p.box.min.x, p.box.max.z),
      along(p.box.max.x, p.box.min.z),
      along(p.box.max.x, p.box.max.z),
    ];
    return {
      node: p.node,
      type: p.type,
      s: along(cx[i]!, cz[i]!),
      lo: Math.min(...corners),
      hi: Math.max(...corners),
      x: cx[i]!,
      z: cz[i]!,
    };
  });
  const length = Math.max(...placed.map((p) => p.hi)) - Math.min(...placed.map((p) => p.lo));
  const tol = length * 0.01;

  /* Supports: the substructure's extents along the axis, merged where they overlap. */
  const sub = placed.filter((p) => SUPPORT_TYPE.test(p.type)).sort((a, b) => a.lo - b.lo);
  if (!sub.length) return [...roots];
  const supports: { lo: number; hi: number; parts: Placed[] }[] = [];
  for (const p of sub) {
    const last = supports[supports.length - 1];
    if (last && p.lo <= last.hi + tol) {
      last.hi = Math.max(last.hi, p.hi);
      last.parts.push(p);
    } else {
      supports.push({ lo: p.lo, hi: p.hi, parts: [p] });
    }
  }
  const centre = supports.map((st) => (st.lo + st.hi) / 2);
  const isAbutment = supports.map((st) => st.parts.some((p) => /abut/i.test(p.type)));
  const piers = centre.filter((_, i) => !isAbutment[i]);

  /* Every other part: across every pier, or in the span its centre is in. */
  const whole: Placed[] = [];
  const spans: Placed[][] = supports.map(() => []);
  spans.push([]);
  for (const p of placed) {
    if (SUPPORT_TYPE.test(p.type)) continue;
    if (piers.length && piers.every((c) => p.lo < c - tol && p.hi > c + tol)) {
      whole.push(p);
      continue;
    }
    spans[centre.filter((c) => c < p.s).length]!.push(p);
  }

  /* In order along the bridge: span 0, support 0, span 1, … */
  const out: ComponentNode[] = [];
  if (whole.length) out.push(group('Whole bridge', byType(whole)));
  let span = 0;
  let abutment = 0;
  let pier = 0;
  for (let i = 0; i <= supports.length; i++) {
    if (spans[i]!.length) out.push(group(`Span ${++span}`, byType(spans[i]!)));
    const st = supports[i];
    if (!st) continue;
    out.push(group(isAbutment[i] ? `Abutment ${++abutment}` : `Pier ${++pier}`, byType(st.parts)));
  }
  return [{ ...top, children: out }];
}

/** Parts under their type groups, in the order the export first named each type. */
function byType(parts: readonly Placed[]): ComponentNode[] {
  const types = new Map<string, Placed[]>();
  for (const p of [...parts].sort((a, b) => a.s - b.s)) {
    const at = types.get(p.type);
    if (at) at.push(p);
    else types.set(p.type, [p]);
  }
  return [...types].map(([type, list]) => group(type, apart(onePerId(list))));
}

/**
 * One row per part: meshes sharing an id AND a name are one part drawn in
 * pieces. The id alone is not enough — every traffic lane of a CSiBridge
 * export carries its deck's.
 */
function onePerId(parts: readonly Placed[]): Placed[] {
  const seen = new Set<string>();
  return parts.filter((p) => {
    if (!p.node.id) return true;
    const key = `${p.node.id}|${p.node.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Where names still repeat, each part under the nearest part whose name does
 * not — the piles under their footing. With not two such parts to tell the
 * repeats apart by, the parts are split into lines across the bridge instead —
 * the diaphragms at each end of a span — and left flat if that does not
 * separate them either. `parts` is in order along the bridge.
 */
function apart(parts: readonly Placed[]): ComponentNode[] {
  const count = new Map<string, number>();
  for (const p of parts) count.set(p.node.label, (count.get(p.node.label) ?? 0) + 1);
  const anchors = parts.filter((p) => count.get(p.node.label) === 1);
  if (anchors.length === parts.length) return byName(parts.map((p) => p.node));
  if (anchors.length < 2) return inLines(parts);
  const under = new Map<Placed, ComponentNode[]>(anchors.map((a) => [a, [a.node]]));
  for (const p of parts) {
    if (under.has(p)) continue;
    let best = anchors[0]!;
    let bestD = Infinity;
    for (const a of anchors) {
      const d = (a.x - p.x) ** 2 + (a.z - p.z) ** 2;
      if (d < bestD) {
        best = a;
        bestD = d;
      }
    }
    under.get(best)!.push(p.node);
  }
  return [...anchors]
    .sort((a, b) => NAME_ORDER.compare(a.node.label, b.node.label))
    .map((a) => group(a.node.label, [a.node, ...byName(under.get(a)!.slice(1))]));
}

const NAME_ORDER = new Intl.Collator(undefined, { numeric: true });

/** "G-1" before "G-2" before "G-10", whatever order the export drew them in. */
function byName(nodes: readonly ComponentNode[]): ComponentNode[] {
  return [...nodes].sort((a, b) => NAME_ORDER.compare(a.label, b.label));
}

/**
 * Parts split where their extents along the bridge stop overlapping, as
 * "Line 1", "Line 2", … in order — or flat where that makes one line.
 */
function inLines(parts: readonly Placed[]): ComponentNode[] {
  const lines: { hi: number; parts: ComponentNode[] }[] = [];
  for (const p of [...parts].sort((a, b) => a.lo - b.lo)) {
    const last = lines[lines.length - 1];
    if (last && p.lo <= last.hi) {
      last.hi = Math.max(last.hi, p.hi);
      last.parts.push(p.node);
    } else {
      lines.push({ hi: p.hi, parts: [p.node] });
    }
  }
  if (lines.length < 2) return byName(parts.map((p) => p.node));
  return lines.map((l, i) => group(`Line ${i + 1}`, byName(l.parts)));
}

/**
 * A node's component id — the source's own object id, which
 * functions/lib/meshGlb.ts writes to `extras.sourceId` and `GLTFLoader` copies
 * into `userData` untouched. Null where the export gave none.
 */
export function nodeId(node: MeshNode): string | null {
  const id = node.userData?.['sourceId'];
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * What a raycast hit is called, in the vocabulary a defect is stored in.
 *
 * The ancestors of the hit node, from `root` down to the node itself, with every
 * COMPONENT's bare label concatenated and every group's contributing nothing —
 * the same rule `walkComponentTree` applies from the top, arrived at from the
 * bottom. A leaf under two groups is therefore its own label, and a mesh nested
 * inside another mesh reads as "Bent-2 Bent Cap", which is what the modeller's
 * own 3D pick writes.
 *
 * `''` when nothing on the chain is a component, which is the honest answer for
 * geometry with no named parts in it: the caller offers no component rather than
 * naming the whole file. `root` itself is not read — it is the loader's `Scene`
 * — and a node that is not under `root` at all walks to the top and returns what
 * it found, rather than looping.
 */
export function qualifiedNameAt(hit: MeshNode, root: MeshNode): string {
  const parts: string[] = [];
  for (let node: MeshNode | null | undefined = hit; node && node !== root; node = node.parent) {
    if (node.isMesh === true) parts.unshift(bareLabel(nodeLabel(node)));
  }
  return parts.join(' ');
}

/**
 * WHICH NODE that name refers to, for a caller that has to do something to it.
 *
 * `qualifiedNameAt` answers what a raycast hit is CALLED; this answers which
 * subtree the name belongs to — what the Defect Inspector highlights when
 * somebody selects a component. Both walk the same chain by the same rule, and
 * they are next to each other for that reason: a selection that lit one part and
 * reported another would be worse than no selection at all.
 *
 * The nearest self-or-ancestor with geometry of its own. A group contributes
 * nothing to the name because it is a work breakdown rather than a thing in the
 * world — so it is not a thing that can be selected either, and tapping through
 * one selects the part inside it.
 *
 * Null in exactly the case `qualifiedNameAt` returns `''`: geometry with no named
 * parts in it. The two cannot disagree about that, because it is the same test on
 * the same chain.
 */
export function componentNodeAt(hit: MeshNode, root: MeshNode): MeshNode | null {
  for (let node: MeshNode | null | undefined = hit; node && node !== root; node = node.parent) {
    if (node.isMesh === true) return node;
  }
  return null;
}

/* ---------------------------------------------------------------------------
   ONE COMPONENT IDENTITY. LIFECYCLE.md `LC-14`.

   A progress entry, a schedule match and a defect each store the label above
   AND, where the model gave one, the component's id beside it. The label is
   what a person reads and what every record made before the id existed is read
   by; the id is what two records are compared on, because a label repeats —
   every pile group of a CSiBridge export has its own "Pile-1" — and an id does
   not. The rule for comparing is `sameComponent`, and it is the one every
   caller uses: ids where both sides have one, labels otherwise.
   --------------------------------------------------------------------------- */

/** How a record names a component: the label always, the id where it has one. */
export interface ComponentRef {
  component: string | null;
  componentId?: string | null;
}

/**
 * Whether two records are about the same part. Ids where both carry one —
 * two different piles both called "Pile-1" are different parts — and the
 * labels otherwise, so an entry made before the id existed still matches.
 */
export function sameComponent(a: ComponentRef, b: ComponentRef): boolean {
  const ia = a.componentId ?? null;
  const ib = b.componentId ?? null;
  if (ia !== null && ib !== null) return ia === ib;
  return a.component !== null && a.component !== '' && a.component === b.component;
}

/**
 * The one id the records naming `label` carry, or null where none carries one
 * or they disagree — two piles both called "Pile-1" give two ids, and choosing
 * between them is a person's job, not this function's.
 */
export function idForLabel(records: readonly ComponentRef[], label: string): string | null {
  let found: string | null = null;
  for (const r of records) {
    if (r.component !== label || !r.componentId) continue;
    if (found !== null && found !== r.componentId) return null;
    found = r.componentId;
  }
  return found;
}

/** One entry of a flat component picker. */
export interface ComponentChoice {
  /** What is stored as the label. */
  name: string;
  /** What is stored beside it, or null on a model with no ids. */
  id: string | null;
  /** What the picker prints: the name, with the id where the name repeats. */
  text: string;
}

/**
 * Every component, in tree order, for a flat picker — one choice per part where
 * the model has ids, so "Pile-3" under each of four pile groups is four choices
 * rather than one that meant all of them. Without ids it is `componentNames`:
 * one choice per name, which is all a label can say.
 */
export function componentChoices(roots: readonly ComponentNode[]): ComponentChoice[] {
  const out: ComponentChoice[] = [];
  const seen = new Set<string>();
  walkComponentTree(roots, ({ node, qualified }) => {
    if (!node.isComponent || qualified === '') return;
    const id = node.id ?? null;
    const key = id ?? `name:${qualified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ name: qualified, id, text: qualified });
  });
  const counts = new Map<string, number>();
  for (const c of out) counts.set(c.name, (counts.get(c.name) ?? 0) + 1);
  for (const c of out) if (c.id !== null && counts.get(c.name)! > 1) c.text = `${c.name} · ${c.id}`;
  return out;
}

/**
 * Every component's qualified name, in tree order, once each.
 *
 * For a picker that is a flat list rather than a tree — the Defect Inspector's,
 * which is one `<select>` on a phone. The order is the tree's own, so parts of
 * one pier stay together and the list reads as a walk over the structure rather
 * than an alphabet.
 */
export function componentNames(roots: readonly ComponentNode[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  walkComponentTree(roots, ({ node, qualified }) => {
    if (!node.isComponent || qualified === '' || seen.has(qualified)) return;
    seen.add(qualified);
    out.push(qualified);
  });
  return out;
}
